import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import { FiDollarSign, FiAlertCircle, FiClock, FiCheckCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { getRetentionLedgerApi } from "../../../../Api/ClientWork/ClientWork";
import { useReportData } from "./useReportData";

const RetentionLedgerReport = () => {
  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const data = useReportData(getRetentionLedgerApi, dateRange);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalRetentionWithheld = data.reduce((s, r) => s + (r.retentionWithheld || 0), 0);
    const dueForClaimAmount = data
      .filter((r) => r.claimStatus === "Due for Claim")
      .reduce((s, r) => s + (r.retentionWithheld || 0), 0);
    const heldByClientAmount = data
      .filter((r) => r.claimStatus === "Held by Client")
      .reduce((s, r) => s + (r.retentionWithheld || 0), 0);
    const receivedAmount = data
      .filter((r) => r.claimStatus === "Received")
      .reduce((s, r) => s + (r.retentionWithheld || 0), 0);

    return {
      totalRetentionWithheld,
      dueForClaimAmount,
      heldByClientAmount,
      receivedAmount,
      dueCount: data.filter((r) => r.claimStatus === "Due for Claim").length,
    };
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Client / Project Name",
        field: "clientProjectName",
        flex: 2,
        minWidth: 240,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Total Contract Value",
        field: "contractValue",
        flex: 1.4,
        minWidth: 150,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Cumulative Retention Withheld (PKR)",
        field: "retentionWithheld",
        flex: 1.6,
        minWidth: 170,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Contract Completion Date",
        field: "completionDate",
        flex: 1.2,
        minWidth: 140,
        valueFormatter: (p) => (p.value ? moment(p.value).format("DD-MMM-YYYY") : "—"),
      },
      {
        headerName: "Defects Liability Period (DLP)",
        field: "dlpPeriod",
        flex: 1.2,
        minWidth: 130,
      },
      {
        headerName: "Retention Due / Maturity Date",
        field: "maturityDate",
        flex: 1.3,
        minWidth: 140,
        valueFormatter: (p) => (p.value ? moment(p.value).format("DD-MMM-YYYY") : "—"),
      },
      {
        headerName: "Claim Status",
        field: "claimStatus",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => {
          const val = p.value || "Held by Client";
          let type = "info";
          if (val === "Received") {
            type = "success";
          } else if (val === "Due for Claim") {
            type = "warning";
          }
          return <StatusChip status={val} type={type} />;
        },
      },
      {
        headerName: "Notes",
        field: "notes",
        flex: 2,
        minWidth: 220,
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.clientProjectName && item.clientProjectName.toLowerCase().includes(q)) ||
        (item.notes && item.notes.toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === "all" || item.claimStatus?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback((field) => {
    if (!gridRef.current?.columnApi) {
      return;
    }
    const col = gridRef.current.columnApi.getColumn(field);
    if (col) {
      const isVisible = col.isVisible();
      gridRef.current.columnApi.setColumnVisible(field, !isVisible);
      setColumnVisibility((prev) => ({ ...prev, [field]: !isVisible }));
    }
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Retention_Money_Ledger_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Retention ledger report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL RETENTION WITHHELD"
            value={`PKR ${(kpis.totalRetentionWithheld / 1000000).toFixed(2)}M`}
            subtitle="Accumulated across projects"
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="DUE FOR IMMEDIATE CLAIM"
            value={`PKR ${(kpis.dueForClaimAmount / 1000000).toFixed(2)}M`}
            subtitle={`${kpis.dueCount} project DLP expired`}
            icon={FiAlertCircle}
            color="#ea580c"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="HELD UNDER ACTIVE DLP"
            value={`PKR ${(kpis.heldByClientAmount / 1000000).toFixed(2)}M`}
            subtitle="Pending completion/DLP"
            icon={FiClock}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="RELEASED & COLLECTED"
            value={`PKR ${(kpis.receivedAmount / 1000000).toFixed(2)}M`}
            subtitle="Post-DLP funds recovered"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Retention Ledger Report"
            >
              Retention Ledger Report
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search project, notes..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "220px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Claim Statuses</option>
                  <option value="held by client">Held by Client</option>
                  <option value="due for claim">Due for Claim</option>
                  <option value="received">Received</option>
                </select>
                <label>Claim Status</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetentionLedgerReport;
